export const RoadmapContent = {
  fundamentos: {
    title: "Fundamentos QA",
    slides: [
        {
        title: "Qué es QA (y qué no es)",
        paragraphs: [
            "Quality Assurance es una disciplina orientada a garantizar la calidad del producto a lo largo de todo su ciclo de vida, no una actividad puntual que se ejecuta únicamente al final del desarrollo.",
            "Incluye procesos, revisiones, validaciones y mecanismos de feedback continuo que permiten reducir riesgos funcionales, técnicos y de negocio antes de que el software llegue a producción.",
            "El testing forma parte del QA, pero QA también influye en cómo se definen los requisitos, se priorizan decisiones técnicas y se asegura que el producto aporte valor real al usuario."
        ]
        },
        {
        title: "Caso de prueba y escenario",
        paragraphs: [
            "Un caso de prueba describe un escenario verificable que define un contexto inicial, una acción concreta y un resultado esperado que puede observarse de forma objetiva.",
            "Debe ser claro, repetible y medible, de modo que diferentes personas puedan ejecutarlo en distintos momentos y obtener el mismo resultado.",
            "Los escenarios bien definidos ayudan a validar comportamientos reales del sistema desde la perspectiva del usuario final y reducen ambigüedades durante la validación."
        ]
        },
        {
        title: "Tipos de pruebas y Smoke testing",
        paragraphs: [
            "Existen distintos tipos de pruebas como unitarias, de integración, de sistema, E2E, regresión y exploratorias, cada una con un propósito específico dentro de la estrategia de calidad.",
            "El smoke testing consiste en un conjunto mínimo de verificaciones diseñadas para comprobar rápidamente que el sistema es estable y funcional a nivel básico.",
            "Si el smoke testing falla, no tiene sentido continuar con pruebas más profundas hasta que los errores críticos que bloquean el uso del sistema sean corregidos."
        ]
        }
    ],
    quiz: [
      { q: "QA se centra principalmente en…", options: ["Buscar bugs", "Asegurar la calidad del producto", "Automatizar tests"], correct: 1 },
      { q: "Testing es…", options: ["Lo mismo que QA", "Una parte de QA", "Un proceso independiente"], correct: 1 },
      { q: "Un caso de prueba debe ser…", options: ["Ambiguo", "Repetible", "Solo técnico"], correct: 1 },
      { q: "Smoke testing se ejecuta…", options: ["Al final", "Antes de todo", "Después de regresión"], correct: 1 },
      { q: "El objetivo principal de QA es…", options: ["Reducir riesgos", "Aumentar bugs", "Reemplazar desarrollo"], correct: 0 }
    ]
  },
  manual: {
    title: "Testing manual",
    slides: [
        {
        title: "Qué es testing manual",
        paragraphs: [
            "El testing manual consiste en validar el comportamiento del sistema sin utilizar automatización, reproduciendo flujos reales de uso de forma controlada.",
            "Este enfoque permite detectar problemas de lógica, usabilidad y experiencia de usuario que suelen pasar desapercibidos en pruebas automatizadas.",
            "Su valor principal reside en el análisis crítico del sistema y en la capacidad del tester para interpretar comportamientos inesperados."
        ]
        },
        {
        title: "Exploratorio vs Scripted",
        paragraphs: [
            "Las pruebas scripted siguen pasos definidos previamente y son especialmente útiles para validaciones repetitivas y pruebas de regresión.",
            "Las pruebas exploratorias se basan en el aprendizaje continuo mientras se interactúa con el sistema, adaptando la estrategia en tiempo real.",
            "Una estrategia de calidad madura combina ambos enfoques para maximizar cobertura y capacidad de descubrimiento."
        ]
        },
        {
        title: "Reporte de bugs efectivo",
        paragraphs: [
            "Un bug report efectivo describe claramente el problema, los pasos necesarios para reproducirlo y la diferencia entre el resultado esperado y el actual.",
            "Debe incluir información relevante del entorno, evidencias visuales o logs y una clasificación adecuada de severidad.",
            "Un reporte poco claro o incompleto retrasa la corrección del error y genera fricción innecesaria entre los miembros del equipo."
        ]
        }
    ],
    quiz: [
      { q: "El testing manual destaca por…", options: ["Ser automático", "Análisis humano", "No necesitar planificación"], correct: 1 },
      { q: "Exploratorio significa…", options: ["Improvisar", "Aprender mientras pruebas", "No documentar"], correct: 1 },
      { q: "Scripted testing es ideal para…", options: ["Regresión", "Descubrimiento", "Pentesting"], correct: 0 },
      { q: "Un bug report debe incluir…", options: ["Solo el título", "Pasos y resultado esperado", "Solo una captura"], correct: 1 },
      { q: "La severidad mide…", options: ["Urgencia", "Impacto técnico", "Prioridad de negocio"], correct: 1 }
    ]
  },
  automation: {
    title: "Automatización de software",
    slides: [
        {
        title: "Qué automatizar y qué no",
        paragraphs: [
            "La automatización permite ejecutar pruebas repetitivas de forma consistente, rápida y fiable a lo largo del tiempo.",
            "No todo debe automatizarse: flujos inestables, en constante cambio o exploratorios suelen aportar más valor cuando se prueban manualmente.",
            "El criterio principal para automatizar debe ser el retorno de inversión, la estabilidad del flujo y su impacto en el producto."
        ]
        },
        {
        title: "Tipos de tests automatizados",
        paragraphs: [
            "Los tests unitarios validan lógica aislada, son rápidos de ejecutar y baratos de mantener cuando están bien diseñados.",
            "Los tests E2E validan flujos completos desde la perspectiva del usuario, pero son más lentos y sensibles a cambios.",
            "Una pirámide de testing equilibrada reduce costes de mantenimiento y aumenta la confianza en el sistema."
        ]
        },
        {
        title: "Buenas prácticas E2E",
        paragraphs: [
            "El uso de selectores estables y semánticos reduce significativamente los falsos fallos en los tests automatizados.",
            "Los tests deben ser independientes, reproducibles y no depender del estado de otros escenarios.",
            "La automatización es código y debe mantenerse con los mismos estándares de calidad que el software de producción."
        ]
        }
    ],
    quiz: [
      { q: "Automatizar sirve para…", options: ["Eliminar QA", "Reducir esfuerzo repetitivo", "Probar todo"], correct: 1 },
      { q: "Los tests más rápidos son…", options: ["E2E", "Unitarios", "Manual"], correct: 1 },
      { q: "La pirámide de testing busca…", options: ["Más E2E", "Equilibrio", "Eliminar manual"], correct: 1 },
      { q: "Un buen selector debe ser…", options: ["Visual", "Estable", "Aleatorio"], correct: 1 },
      { q: "La automatización es…", options: ["Solo tests", "Código mantenible", "Opcional"], correct: 1 }
    ]
  },
  api: {
    title: "Automatización de software",
    slides: [
        {
        title: "Qué automatizar y qué no",
        paragraphs: [
            "La automatización permite ejecutar pruebas repetitivas de forma consistente, rápida y fiable a lo largo del tiempo.",
            "No todo debe automatizarse: flujos inestables, en constante cambio o exploratorios suelen aportar más valor cuando se prueban manualmente.",
            "El criterio principal para automatizar debe ser el retorno de inversión, la estabilidad del flujo y su impacto en el producto."
        ]
        },
        {
        title: "Tipos de tests automatizados",
        paragraphs: [
            "Los tests unitarios validan lógica aislada, son rápidos de ejecutar y baratos de mantener cuando están bien diseñados.",
            "Los tests E2E validan flujos completos desde la perspectiva del usuario, pero son más lentos y sensibles a cambios.",
            "Una pirámide de testing equilibrada reduce costes de mantenimiento y aumenta la confianza en el sistema."
        ]
        },
        {
        title: "Buenas prácticas E2E",
        paragraphs: [
            "El uso de selectores estables y semánticos reduce significativamente los falsos fallos en los tests automatizados.",
            "Los tests deben ser independientes, reproducibles y no depender del estado de otros escenarios.",
            "La automatización es código y debe mantenerse con los mismos estándares de calidad que el software de producción."
        ]
        }
    ],
    quiz: [
      { q: "Una API sirve para…", options: ["Mostrar UI", "Comunicar sistemas", "Diseñar UX"], correct: 1 },
      { q: "Un código 200 indica…", options: ["Error", "Éxito", "Redirección"], correct: 1 },
      { q: "Validar APIs es clave porque…", options: ["Son invisibles", "Son la base", "No fallan"], correct: 1 },
      { q: "API testing se enfoca en…", options: ["Frontend", "Backend", "UX"], correct: 1 },
      { q: "Newman se usa para…", options: ["UI", "CI", "Deploy"], correct: 1 }
    ]
  },
  cicd: {
    title: "CI/CD para QA",
    slides: [
        {
        title: "Qué es CI/CD",
        paragraphs: [
            "CI/CD automatiza la integración y entrega continua del software en entornos controlados.",
            "Permite detectar errores de forma temprana y reducir el riesgo de introducir fallos en producción.",
            "QA juega un papel fundamental asegurando que la calidad se mantenga a lo largo del pipeline."
        ]
        },
        {
        title: "Tests en pipelines",
        paragraphs: [
            "Los tests se ejecutan automáticamente ante cada cambio en el código o despliegue.",
            "Un fallo en los tests bloquea el avance del pipeline hasta que el problema se resuelve.",
            "Esto protege la calidad del producto antes de que llegue a los usuarios finales."
        ]
        },
        {
        title: "Quality Gates",
        paragraphs: [
            "Los quality gates definen criterios mínimos que un build debe cumplir para ser aceptado.",
            "Pueden incluir métricas como cobertura de tests, resultados automáticos o calidad del código.",
            "Sin quality gates, la automatización pierde efectividad y control."
        ]
        }
    ],
    quiz: [
      { q: "CI significa…", options: ["Continuous Integration", "Code Injection", "Cloud Infra"], correct: 0 },
      { q: "QA en CI/CD sirve para…", options: ["Bloquear", "Proteger calidad", "Retrasar"], correct: 1 },
      { q: "Un pipeline ejecuta…", options: ["Tests manuales", "Tests automáticos", "Ninguno"], correct: 1 },
      { q: "Quality gates evitan…", options: ["Deploys defectuosos", "Tests", "Código"], correct: 0 },
      { q: "CI/CD mejora…", options: ["Velocidad y calidad", "Solo velocidad", "Solo QA"], correct: 0 }
    ]
  },
  buenas: {
    title: "Buenas prácticas",
    slides: [
        {
        title: "Arquitectura de tests",
        paragraphs: [
            "Una buena arquitectura de tests facilita el mantenimiento y la escalabilidad del proyecto a largo plazo.",
            "Separar lógica, datos, utilidades y configuración reduce la deuda técnica acumulada.",
            "Una mala arquitectura convierte la suite de tests en un problema en lugar de una ayuda."
        ]
        },
        {
        title: "Flakiness y estabilidad",
        paragraphs: [
            "Los tests flaky fallan de forma intermitente sin una causa real en el sistema.",
            "Este comportamiento reduce la confianza del equipo en los resultados de la automatización.",
            "Eliminar flakiness debe ser una prioridad para mantener credibilidad en la suite."
        ]
        },
        {
        title: "Mentalidad QA",
        paragraphs: [
            "QA no actúa como policía ni como obstáculo para el desarrollo.",
            "Su rol es facilitar la calidad y aportar visión crítica al equipo.",
            "La calidad es una responsabilidad compartida entre todos los roles."
        ]
        }
    ],
    quiz: [
      { q: "Una buena arquitectura de tests…", options: ["Complica", "Facilita mantenimiento", "Es opcional"], correct: 1 },
      { q: "Un test flaky es…", options: ["Útil", "Inestable", "Rápido"], correct: 1 },
      { q: "La calidad es responsabilidad de…", options: ["QA", "Dev", "Todo el equipo"], correct: 2 },
      { q: "Eliminar flakiness es importante porque…", options: ["Consume tiempo", "Reduce confianza", "Es moda"], correct: 1 },
      { q: "QA moderno actúa como…", options: ["Policía", "Facilitador", "Auditor"], correct: 1 }
    ]
  }
};
